import { useCallback, useEffect, useState } from "react";

//Common
import {
    Button,
    ConfirmationDialog,
    LoadingIndicator,
    Snackbar,
} from "../../common/VFX-UI";

//App
import {
    EmployeePledgeViewModel,
    createEmployeePledgeViewModel,
    initializeEmployeePledgeViewModel,
} from "../../../viewModels";
import EmployeePledgeForm from "./EmployeePledgeForm";
import {
    useEmployeePledgeLookupsQuery,
    useEmployeePledgeQuery,
    useCampaignQuery,
    useContributorQuery,
    useSaveEmployeePledge,
} from "./hooks/useEmployeePledge";
import EmployeePledgeSummary from "./Summary";
import { useIdleTimer } from "react-idle-timer";

//Helpers
import { getContributorId } from "../../../utility/helpers/claimHelper";
import { EMPTY_GUID } from "../../../utility/constants";
import { useNavigate, useParams } from "react-router-dom";
import usePledgeStyles from "./styles";
import { SnackbarState } from "../../../types";
import { ArrowLeft, Printer } from "react-bootstrap-icons";

const EmployeePledgeFormContainer = () => {
    const navigate = useNavigate();
    const { campaignId } = useParams();
    const pledgeStyles = usePledgeStyles();
    const [showSummary, setShowSummary] = useState(false);
    const [printSummary, setPrintSummary] = useState<(() => void) | null>(null);
    const handlePrintReady = useCallback(
        (print: () => void) => setPrintSummary(() => print),
        []
    );
    const [employeePledgeDetails, setEmployeePledgeDetails] = useState(
        new EmployeePledgeViewModel()
    );
    const [confirmationPledgeDetails, setConfirmationPledgeDetails] = useState(
        new EmployeePledgeViewModel()
    );
    const [confirmDonation, setConfirmDonation] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        showErrorSnackbar: false,
        errorMessage: "",
    });
    const onShowErrorSnackbar = (errorMessage: string) =>
        setSnackbar({ showErrorSnackbar: true, errorMessage });
    const onCloseSnackbar = () =>
        setSnackbar({ showErrorSnackbar: false, errorMessage: "" });

    const pledgeLookupsQuery = useEmployeePledgeLookupsQuery();
    const campaignQuery = useCampaignQuery(campaignId);
    const contributorQuery = useContributorQuery(getContributorId());
    const pledgeQuery = useEmployeePledgeQuery(campaignId);
    const { status, mutate, isError, error, reset } = useSaveEmployeePledge();
    const hasExistingPledgeSummary = Boolean(
        pledgeQuery.data?.result?.hasResponded &&
        pledgeQuery.data?.result?.isWillingToContribute
    );

    useEffect(() => {
        if (!pledgeQuery?.isLoading && pledgeQuery.data?.result?.id) {
            const employeePledgeDetails = createEmployeePledgeViewModel(
                pledgeQuery.data.result
            );
            setEmployeePledgeDetails(employeePledgeDetails);
            const showSummary =
                pledgeQuery.data?.result?.hasResponded &&
                pledgeQuery.data?.result?.isWillingToContribute;
            setShowSummary(showSummary ?? false);
        }
    }, [pledgeQuery.isLoading, pledgeQuery.data]);

    useEffect(() => {
        if (pledgeQuery.isError) {
            navigate("/active-campaigns", { replace: true });
        }
    }, [navigate, pledgeQuery.isError]);

    useEffect(() => {
        if (isError && error) {
            const apiError = error as unknown as {
                messages?: Array<{ message?: string }>;
                errorMessages?: string[];
                message?: string;
            };
            const hasGlobalApiAlert =
                apiError.messages?.some((item) => Boolean(item?.message?.trim())) ||
                apiError.errorMessages?.some((message) => Boolean(message?.trim()));

            if (!hasGlobalApiAlert) {
                const fallbackMessage = apiError.message?.trim();
                if (fallbackMessage) {
                    onShowErrorSnackbar(fallbackMessage);
                }
            }
            reset(); // so repeated errors can re-trigger the effect
        }
    }, [isError, error, reset]);

    const handleOnSubmit = async (values: EmployeePledgeViewModel) => {
        let payload = values;
        if (values.isNotWillingToContribute) {
            payload.giftInvestmentAmount = [];
            payload.contributionAmount = 0;
            payload.giftInvestment = [];
            payload.paymentOptionId = null;
            payload.spouseName = "";
            payload.spousePledgeAmount = null;
            payload.spouseWorkPlace = "";
        }
        setEmployeePledgeDetails(payload);

        payload.giftInvestmentAmount = payload.giftInvestmentAmount?.filter(
            (investment) => typeof investment.giftInvestmentId !== "object"
        ) ?? [];

        const confirmation = initializeEmployeePledgeViewModel(payload);
        if (!confirmation?.isNotWillingToContribute) {
            confirmation.giftInvestment = payload.giftInvestmentAmount?.filter(
                (investment) =>
                    !(
                        investment.giftInvestment?.toLocaleLowerCase() === "other" &&
                        investment.agencyId === null
                    )
            ) ?? [];
        }
        setConfirmationPledgeDetails(confirmation);
        setConfirmDonation(true);
    };

    const handleOnSave = () => {
        if (employeePledgeDetails) {
            const payload = {
                ...employeePledgeDetails,
                giftInvestment: employeePledgeDetails.giftInvestmentAmount?.filter(
                    (investment) =>
                        !(
                            investment.giftInvestment?.toLocaleLowerCase() === "other" &&
                            investment.agencyId === null
                        )
                ) ?? [],
            };
            const model = initializeEmployeePledgeViewModel(
                Object.assign(new EmployeePledgeViewModel(), payload)
            );
            const existingPledgeId = pledgeQuery.data?.result?.id;
            model.id = existingPledgeId && existingPledgeId !== EMPTY_GUID
                ? existingPledgeId
                : EMPTY_GUID;
            model.contributorId = model.contributorId && model.contributorId !== EMPTY_GUID
                ? model.contributorId
                : getContributorId();
            model.campaignId = model.campaignId && model.campaignId !== EMPTY_GUID
                ? model.campaignId
                : campaignId ?? null;
            model.subscriptionId = model.subscriptionId || null;
            const employeePledge = model.toDto();
            employeePledge.hasResponded = true;
            if (employeePledge) mutate(employeePledge);
        }
    };

    const onIdle = () => {
        const origin = window.location.origin;
        window.location.href = origin + "/logout";
    };

    useIdleTimer({
        onIdle,
        timeout: 5 * 60 * 1000,
        throttle: 500,
    });

    if (
        pledgeQuery.isLoading ||
        pledgeQuery.isError ||
        (hasExistingPledgeSummary && !showSummary)
    ) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <div
                style={
                    showSummary
                        ? pledgeStyles.summaryNavigation
                        : pledgeStyles.backButtonContainer
                }
            >
                <div className={showSummary ? "" : "pb-3"}>
                    <Button
                        onClick={() => navigate(-1)}
                        style={pledgeStyles.summaryBackButton}
                        className="btn"
                    >
                        <ArrowLeft aria-hidden="true" />
                        Back to campaigns
                    </Button>
                </div>
                {/*{showSummary && printSummary && (*/}
                {/*    <Button*/}
                {/*        onClick={printSummary}*/}
                {/*        style={pledgeStyles.summaryBackButton}*/}
                {/*        className="btn"*/}
                {/*    >*/}
                {/*        <Printer aria-hidden="true" />*/}
                {/*        Print summary*/}
                {/*    </Button>*/}
                {/*)}*/}
            </div>

            {!showSummary ? (
                <>
                    <EmployeePledgeForm
                        pledgeQuery={pledgeQuery?.data?.result}
                        lookups={pledgeLookupsQuery.data?.result}
                        employee={contributorQuery.data?.result}
                        campaign={campaignQuery.data?.result}
                        initialVal={employeePledgeDetails}
                        status={status}
                        onClose={() => { }}
                        handleOnSubmit={handleOnSubmit}
                    />
                </>
            ) : (
                <EmployeePledgeSummary
                    summary={employeePledgeDetails}
                    lookups={pledgeLookupsQuery.data?.result}
                    onPrintReady={handlePrintReady}
                />
            )}

            <ConfirmationDialog
                size="lg"
                open={confirmDonation}
                onClose={() => setConfirmDonation(false)}
                onConfirm={() => {
                    setConfirmDonation(false);
                    handleOnSave();
                }}
                contentTitle={"Your Gift"}
                contentText={
                    "Are you ready to proceed with this gift? Once you submit only an admin can change the designation."
                }
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            >
                <EmployeePledgeSummary
                    summary={confirmationPledgeDetails}
                    lookups={pledgeLookupsQuery.data?.result}
                    isConfirmation
                />
            </ConfirmationDialog>
            {snackbar.showErrorSnackbar && (
                <Snackbar
                    open={true}
                    message={snackbar.errorMessage}
                    handleClose={onCloseSnackbar}
                    severity="danger"
                />
            )}
        </>
    );
};

export default EmployeePledgeFormContainer;
